/**
 * DebugTraceUtils — Utilities for debug trace visualization
 */

import {
  IconPlayerPlay,
  IconPlayerStop,
  IconArrowRight,
  IconArrowLeft,
  IconRefresh,
  IconGitBranch,
  IconFileText,
  IconBrain,
  IconMessage,
  IconPhoto,
  IconBolt,
  IconCheck,
  IconTool,
  IconCheckbox,
  IconSearch,
  IconAlertTriangle,
} from '@tabler/icons-react';

export const EVENT_CONFIG = {
  'graph.start':          { icon: IconPlayerPlay,     color: 'var(--primary, #6366f1)', label: 'Início' },
  'graph.end':            { icon: IconPlayerStop,     color: 'var(--primary, #6366f1)', label: 'Fim' },
  'node.enter':           { icon: IconArrowRight,     color: '#3b82f6', label: 'Entrou no nó' },
  'node.exit':            { icon: IconArrowLeft,      color: '#3b82f6', label: 'Saiu do nó' },
  'node.loop_detected':   { icon: IconRefresh,        color: '#ef4444', label: 'Loop detectado' },
  'condition.evaluate':   { icon: IconGitBranch,      color: '#8b5cf6', label: 'Condição' },
  'capability.prompt_built': { icon: IconFileText,    color: '#06b6d4', label: 'Prompt montado' },
  'capability.llm_call':  { icon: IconBrain,          color: '#f59e0b', label: 'Chamada LLM' },
  'capability.response':  { icon: IconMessage,        color: '#10b981', label: 'Resposta LLM' },
  'capability.media_check': { icon: IconPhoto,        color: '#6366f1', label: 'Análise de mídia' },
  'action.execute':       { icon: IconBolt,           color: '#f97316', label: 'Ação executada' },
  'action.response':      { icon: IconCheck,          color: '#10b981', label: 'Resposta da ação' },
  'tool.call':            { icon: IconTool,           color: '#ec4899', label: 'Tool chamada' },
  'tool.result':          { icon: IconCheckbox,       color: '#10b981', label: 'Resultado tool' },
  'rag.query':            { icon: IconSearch,         color: '#14b8a6', label: 'Consulta RAG' },
  'error':                { icon: IconAlertTriangle,  color: '#ef4444', label: 'Erro' },
};

const DEPTH_MAP = {
  'graph': 0,
  'node': 1,
  'condition': 1,
  'capability': 2,
  'action': 2,
  'tool': 3,
  'rag': 3,
  'error': 2,
};

/**
 * Compute indentation depth for each event based on its type prefix.
 * Returns a new array of { ...event, depth } objects.
 */
export function computeDepth(events) {
  if (!events || !Array.isArray(events)) return [];
  return events.map((event) => {
    const prefix = event.event_type?.split('.')[0] || '';
    const depth = DEPTH_MAP[prefix] ?? 1;
    return { ...event, depth };
  });
}

/**
 * Format milliseconds into a human-readable duration string.
 */
export function formatDuration(ms) {
  if (ms == null) return null;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

/**
 * Extract a compact summary string from event data for inline display.
 */
export function formatEventData(event) {
  if (!event) return '';
  const { event_type, data } = event;
  if (!data) return '';

  switch (event_type) {
    case 'graph.start':
      return data.entry ? `entry: ${data.entry}` : '';
    case 'graph.end':
      return data.exit_node ? `exit: ${data.exit_node}` : '';
    case 'node.enter':
    case 'node.exit':
      return event.data?.node_label || event.node_key || event.node_id || '';
    case 'node.loop_detected':
      return data.node_key || '';
    case 'condition.evaluate': {
      const result = data.result != null ? String(data.result) : '';
      const score = data.score != null ? ` (${Number(data.score).toFixed(2)})` : '';
      return `→ ${result}${score}`;
    }
    case 'capability.prompt_built':
      return data.tool_count != null ? `${data.tool_count} tools` : '';
    case 'capability.llm_call':
      return data.model || '';
    case 'capability.response': {
      const text = data.response || data.text || '';
      return text.length > 60 ? `"${text.slice(0, 57)}..."` : text ? `"${text}"` : '';
    }
    case 'capability.media_check':
      return data.media_type || '';
    case 'action.execute':
      return data.action_name || data.action_type || '';
    case 'action.response':
      return data.status || '';
    case 'tool.call':
      return data.tool_name || '';
    case 'tool.result':
      return data.status || (data.success ? 'success' : '');
    case 'rag.query':
      return data.query ? (data.query.length > 50 ? `${data.query.slice(0, 47)}...` : data.query) : '';
    case 'error':
      return data.message || data.error || '';
    default:
      return '';
  }
}
